import type { PolicyCheckData } from '../../shared/agent-consent/domain/policyTypes';
import type {
  McpPolicyData,
  McpPolicyRawToolset,
  McpPolicyIntentGroup,
} from './policyTypes';

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

/** Parse a raw toolset array from the API (byIntent.toolsets). */
function parseRawToolsetArray(items: unknown): McpPolicyRawToolset[] {
  if (!Array.isArray(items)) return [];
  const result: McpPolicyRawToolset[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    const product = typeof o.product === 'string' ? o.product.trim() : '';
    const intent = typeof o.intent === 'string' ? o.intent.trim() : '';
    const description = typeof o.description === 'string' ? o.description : '';
    const scopes = Array.isArray(o.scopes)
      ? o.scopes
          .filter((s): s is string => typeof s === 'string')
          .sort(collator.compare)
      : [];
    if (name) {
      result.push({ name, product, intent, scopes, description });
    }
  }
  result.sort((a, b) => collator.compare(a.name, b.name));
  return result;
}

/** Parse byIntent from the ordered-array format. */
function parseByIntent(byIntent: unknown): McpPolicyIntentGroup[] {
  if (!Array.isArray(byIntent)) return [];
  const result: McpPolicyIntentGroup[] = [];
  for (const item of byIntent) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const id = typeof o.id === 'string' ? o.id.trim() : '';
    const label = typeof o.label === 'string' ? o.label : '';
    const description = typeof o.description === 'string' ? o.description : '';
    const toolsets = parseRawToolsetArray(o.toolsets);
    if (id) {
      result.push({ id, label, description, toolsets });
    }
  }
  result.sort((a, b) => collator.compare(a.label, b.label));
  return result;
}

/**
 * Extends the shared PolicyCheckData with MCP-specific toolset parsing.
 * Extracts byIntent from the raw response body.
 */
export function parseMcpPolicyData(sharedData: PolicyCheckData): McpPolicyData {
  const toolsets = sharedData.rawBody.toolsets as
    | Record<string, unknown>
    | undefined;
  const byIntent = parseByIntent(toolsets?.byIntent);
  const crossOrgScopesRemoved =
    typeof sharedData.rawBody.crossOrgScopesRemoved === 'boolean'
      ? sharedData.rawBody.crossOrgScopesRemoved
      : false;

  return {
    domain: sharedData.domain,
    byIntent,
    redirectAllowed: sharedData.redirectAllowed,
    crossOrgScopesRemoved,
  };
}
